import { useEffect } from "react"

// Measure intrinsic content, never the min-height wrappers that we size. This
// lets cards shrink again after a font/viewport/data change without a resize loop.
export default function useUniformMatchCards(rootRef, data, ready) {
  useEffect(() => {
    const root = rootRef.current
    if (!root || !ready) return undefined
    const nodes = [...root.querySelectorAll("[data-match-size]")]
    let active = true
    let frame
    const keys = ["context", "side", "score"]
    const clear = () => {
      ;[...keys, "total"].forEach(key =>
        root.style.removeProperty(`--match-${key}-height`)
      )
    }
    const measure = () => {
      if (!active) return
      // Cards inside a zoomed bracket report zoomed client rects; the CSS variables
      // are consumed inside that same zoom, so store unzoomed heights.
      const height = node =>
        node.getBoundingClientRect().height / (node.currentCSSZoom ?? 1)
      const heights = Object.fromEntries(
        keys.map(key => [
          key,
          Math.ceil(
            Math.max(
              0,
              ...nodes
                .filter(node => node.dataset.matchSize === key)
                .map(height)
            )
          ),
        ])
      )
      if (!heights.context) {
        clear()
        return
      }
      keys.forEach(key =>
        root.style.setProperty(`--match-${key}-height`, `${heights[key]}px`)
      )
      root.style.setProperty(
        "--match-total-height",
        `${heights.context + 2 * heights.side + heights.score + 2}px`
      )
    }
    const schedule = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(measure)
    }
    measure()
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(schedule)
    nodes.forEach(node => observer?.observe(node))
    window.addEventListener("resize", schedule)
    document.fonts?.ready.then(() => {
      if (active) schedule()
    })
    return () => {
      active = false
      cancelAnimationFrame(frame)
      observer?.disconnect()
      window.removeEventListener("resize", schedule)
      clear()
    }
  }, [rootRef, data, ready])
}
